import { CalendarDurationLink } from './CalendarDurationLink';
import { CalendarPerformanceChart } from './CalendarPerformanceChart';
import { useEffect, useState } from 'react';
import { IoTimeOutline } from 'react-icons/io5';
import { useTranslation } from 'react-i18next';
import { home, type CalendarDayEntry, type TodayTrends } from '../../services/api';
import { atpApi, type ATPWeek } from '../../services/atpApi';
import { calendarPlanMetrics } from '../../utils/calendarPlanMetrics';
import { displayDigits } from '../../utils/displayDigits';
import './calendar-plan-summary.css';

export function CalendarPlanSummary({ userId, dates, days, refreshToken, weeks: suppliedWeeks, loading = false, weekly = false, onPlanChange, planPath = '/annual-plan', onOpenPlan }: {
  userId: number | null; dates: string[]; days: CalendarDayEntry[]; refreshToken?: unknown;
  weeks?: ATPWeek[]; loading?: boolean; weekly?: boolean; onPlanChange?: (hasPlan: boolean) => void; planPath?: string; onOpenPlan?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [chartOpen, setChartOpen] = useState(false);
  const [plan, setPlan] = useState<{ userId: number; weeks: ATPWeek[] } | null>(null);
  const [current, setCurrent] = useState<{ userId: number; trends: TodayTrends | null } | null>(null);
  useEffect(() => {
    let live = true;
    setPlan(null);
    if (userId && suppliedWeeks === undefined) void atpApi.getAtp(userId)
      .then(result => { if (live) setPlan({ userId, weeks: result?.weeks ?? [] }); }).catch(() => {});
    return () => { live = false; };
  }, [userId, refreshToken, suppliedWeeks]);
  const weeks = suppliedWeeks ?? (plan?.userId === userId ? plan.weeks : []);
  const hasPlan = weeks.length > 0;
  useEffect(() => { onPlanChange?.(hasPlan); }, [hasPlan, onPlanChange]);
  useEffect(() => {
    let live = true;
    setCurrent(null);
    if (userId && hasPlan) void home.getHomeData(userId)
      .then(result => { if (live) setCurrent({ userId, trends: result.data.data.today_trends }); }).catch(() => {});
    return () => { live = false; };
  }, [userId, hasPlan, refreshToken]);
  if (!hasPlan) return null;
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const totals = calendarPlanMetrics(dates, weeks, days, today);
  const trends = current?.userId === userId ? current.trends : null;
  const format = (value: unknown) => value == null || value === '' || !Number.isFinite(Number(value))
    ? '—' : displayDigits(String(Math.round(Number(value))), i18n.language);
  const visible = new Set(dates);
  const workouts = days.filter(day => visible.has(day.day.slice(0, 10))).flatMap(day => day.workouts ?? []);
  const duration = workouts.reduce((sum, w) => sum + (Number(w.total_time ?? w.total_time_planned) || 0), 0);
  const durationLabel = `${Math.floor(duration / 3600)}:${String(Math.floor(duration % 3600 / 60)).padStart(2, '0')}:${String(Math.floor(duration % 60)).padStart(2, '0')}`;
  const progress = !loading && totals.actual != null && totals.planned != null && totals.planned > 0
    ? totals.actual / totals.planned * 100 : null;
  return <section className="calendar-plan-summary" aria-label={t(weekly ? 'calendar.planSummary.weekTitle' : 'calendar.planSummary.title')}>
    <div className="calendar-plan-summary__overview">
      <span className="calendar-plan-summary__count"><span>{t('records.workout')}</span><bdi>{loading ? '—' : displayDigits(String(workouts.length), i18n.language)}</bdi></span>
      <CalendarDurationLink workouts={workouts}><IoTimeOutline aria-hidden="true" /><span className="calendar-plan-summary__duration"><span>{t('calendar.studio.duration')}</span><bdi>{loading ? '—' : displayDigits(durationLabel, i18n.language)}</bdi></span></CalendarDurationLink>
    </div>
    <a className="calendar-plan-summary__load" href={planPath} title={t('annualPlan.monitor.backToPlan')}
      onClick={event => { if (onOpenPlan && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && event.button === 0) { event.preventDefault(); onOpenPlan(); } }}>
      <dl className="calendar-plan-summary__load-values">
        <div><dt>{t('calendar.planSummary.plannedShort')}</dt><dd><bdi>{format(loading ? null : totals.planned)}</bdi></dd></div>
        <div><dt>{t('calendar.planSummary.actualShort')}</dt><dd><bdi>{format(loading ? null : totals.actual)}</bdi></dd></div>
      </dl>
      <div className="calendar-plan-summary__progress">
        <div className="calendar-plan-summary__track" aria-hidden="true">
          {progress != null && <span style={{ width: `${Math.min(100, progress)}%` }} />}
        </div>
      </div>
    </a>
    <a className="calendar-plan-summary__metrics-link" href="#calendar-performance-chart" aria-haspopup="dialog" title={t('charts.pmcSplit.title')} onClick={event => { event.preventDefault(); setChartOpen(true); }}>
    <dl className="calendar-plan-summary__metrics">
      <div><dt>{t('calendar.planSummary.fitnessShort')}</dt><dd><bdi>{format(trends?.ctl)}</bdi></dd></div>
      <div><dt>{t('calendar.planSummary.formShort')}</dt><dd className={trends?.tsb != null && Number(trends.tsb) < 0 ? 'calendar-plan-summary__negative' : trends?.tsb != null && Number(trends.tsb) > 0 ? 'calendar-plan-summary__positive' : undefined}><bdi>{format(trends?.tsb)}</bdi></dd></div>
      <div><dt>{t('calendar.planSummary.fatigueShort')}</dt><dd><bdi>{format(trends?.atl)}</bdi></dd></div>
    </dl>
    </a>
    {chartOpen && userId != null && <CalendarPerformanceChart key={userId} userId={userId} onClose={() => setChartOpen(false)} />}
  </section>;
}
